import { z } from 'zod';
import { LEAD_SOURCES } from './crm';
import { RecordIdSchema } from './identifiers';
import { MoneySchema } from './money';
import type { REFERENCE_LISTS } from './settings';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Sales Admin / مسؤول CRM workspace (2026-11-02, ADR-0049).
 *
 * Every figure here is computed by the server inside the reader's scope, by the same filter the list
 * it opens reads, and every salesperson's result comes from recorded source rows: activities credit
 * who acted, assignments credit who received, reservations and contracts credit their sales owner.
 */

/** The only job title the restricted team action creates. Fixed on the server, never a request field. */
export const SALES_REPRESENTATIVE_ROLE = 'sales-representative';

/**
 * Add a salesperson. The body has no role, permission or scope field and unknown fields are refused:
 * a forged request cannot ask for more than this one job title, inside the caller's own scope.
 */
export const AddSalesRepresentativeSchema = z.strictObject({
  displayName: z.string().trim().min(2).max(200),
  email: z.string().trim().email().max(254),
  /** Checked against the password policy, hashed at once, and to be changed at first sign-in. */
  initialPassword: z.string().min(1).max(200),
  /** A retry of the same submission answers with the same account; it never takes one over. */
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type AddSalesRepresentative = z.infer<typeof AddSalesRepresentativeSchema>;

export const SalesTeamMemberSchema = z.strictObject({
  accountId: z.string(),
  displayName: z.string(),
  loginIdentifier: z.string(),
  state: z.string(),
  passwordChangeRequired: z.boolean(),
  lastLoginAt: InstantSchema.optional(),
  assignedLeads: z.number().int().nonnegative(),
  openLeads: z.number().int().nonnegative(),
  interested: z.number().int().nonnegative(),
  notInterested: z.number().int().nonnegative(),
  noAnswer: z.number().int().nonnegative(),
  followUpsToday: z.number().int().nonnegative(),
  followUpsUpcoming: z.number().int().nonnegative(),
  followUpsOverdue: z.number().int().nonnegative(),
  followUpsCompleted: z.number().int().nonnegative(),
});
export type SalesTeamMember = z.infer<typeof SalesTeamMemberSchema>;

export const SalesTeamSchema = z.strictObject({
  items: z.array(SalesTeamMemberSchema),
});
export type SalesTeam = z.infer<typeof SalesTeamSchema>;

export const AddSalesRepresentativeResultSchema = z.strictObject({
  member: SalesTeamMemberSchema,
  /** False when this answered a retry of a submission that had already succeeded. */
  created: z.boolean(),
});

/* ---------------------------------------------------------------- dashboard */

export const SalesAdminDashboardQuerySchema = z
  .strictObject({
    from: BusinessDateSchema.optional(),
    to: BusinessDateSchema.optional(),
  })
  .refine((query) => !query.from || !query.to || query.from <= query.to, {
    message: 'RANGE_INVERTED',
  });
export type SalesAdminDashboardQuery = z.infer<typeof SalesAdminDashboardQuerySchema>;

/**
 * `current` is the state of the work right now, whatever the period; `period` counts what happened
 * inside it. A lead is counted once however many activities or contracts it has.
 */
export const SalesAdminDashboardSchema = z.strictObject({
  today: BusinessDateSchema,
  period: z.strictObject({ from: BusinessDateSchema, to: BusinessDateSchema }),
  current: z.strictObject({
    totalLeads: z.number().int().nonnegative(),
    unassigned: z.number().int().nonnegative(),
    assigned: z.number().int().nonnegative(),
    interested: z.number().int().nonnegative(),
    notInterested: z.number().int().nonnegative(),
    followUpsToday: z.number().int().nonnegative(),
    followUpsOverdue: z.number().int().nonnegative(),
    followUpsUpcoming: z.number().int().nonnegative(),
    meetingsScheduled: z.number().int().nonnegative(),
    visitsScheduled: z.number().int().nonnegative(),
  }),
  activity: z.strictObject({
    newLeadsToday: z.number().int().nonnegative(),
    newLeads: z.number().int().nonnegative(),
    confirmedReservations: z.number().int().nonnegative(),
    activatedContracts: z.number().int().nonnegative(),
  }),
});
export type SalesAdminDashboard = z.infer<typeof SalesAdminDashboardSchema>;

/**
 * Each lead card's definition, as the lead list's own query parameters. The server counts with these
 * and the card opens the list with these, so a card and its list cannot disagree.
 */
export const SALES_ADMIN_LEAD_CARDS = {
  totalLeads: {},
  unassigned: { assignment: 'unassigned' },
  assigned: { assignment: 'assigned' },
  interested: { statuses: 'interested' },
  notInterested: { statuses: 'notInterested' },
  followUpsToday: { followUp: 'today' },
  followUpsOverdue: { followUp: 'overdue' },
  followUpsUpcoming: { followUp: 'upcoming' },
  meetingsScheduled: { nextFollowUpKind: 'meeting' },
  visitsScheduled: { nextFollowUpKind: 'siteVisit' },
} as const;

/* --------------------------------------------------------------- follow-ups */

/** The team follow-up view's categories. Each one is a filter on the lead list except `completed`. */
export const FOLLOW_UP_VIEWS = [
  'today',
  'upcoming',
  'overdue',
  'completed',
  'noAnswer',
  'repeatedNoAnswer',
  'callback',
  'noActivity',
] as const;
export type FollowUpView = (typeof FOLLOW_UP_VIEWS)[number];

export const FollowUpFiltersSchema = z.strictObject({
  assignees: z
    .string()
    .max(2100)
    .transform((value) => value.split(',').filter(Boolean).slice(0, 50))
    .optional(),
  statuses: z
    .string()
    .max(400)
    .transform((value) => value.split(',').filter(Boolean))
    .optional(),
  interestedProjectId: RecordIdSchema.optional(),
  followUpFrom: BusinessDateSchema.optional(),
  followUpTo: BusinessDateSchema.optional(),
});
export type FollowUpFilters = z.infer<typeof FollowUpFiltersSchema>;

/** A count that needs a setting nobody has entered is null, never a real zero. */
export const FollowUpSummarySchema = z.strictObject({
  today: z.number().int().nonnegative(),
  upcoming: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  noAnswer: z.number().int().nonnegative(),
  repeatedNoAnswer: z.number().int().nonnegative().nullable(),
  callback: z.number().int().nonnegative(),
  noActivity: z.number().int().nonnegative().nullable(),
});
export type FollowUpSummary = z.infer<typeof FollowUpSummarySchema>;

export const CompletedFollowUpQuerySchema = FollowUpFiltersSchema.extend({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
export type CompletedFollowUpQuery = z.infer<typeof CompletedFollowUpQuerySchema>;

/** A completed follow-up: what was scheduled against when a person recorded it done. */
export const CompletedFollowUpSchema = z.strictObject({
  activityId: z.string(),
  leadId: z.string(),
  leadNumber: z.string().optional(),
  leadName: z.string(),
  actorAccountId: z.string().optional(),
  kind: z.string(),
  result: z.string().optional(),
  scheduledOn: BusinessDateSchema,
  scheduledTime: z.string().optional(),
  scheduledKind: z.string().optional(),
  completedAt: InstantSchema,
  onTime: z.boolean(),
  note: z.string().optional(),
});
export type CompletedFollowUp = z.infer<typeof CompletedFollowUpSchema>;

export const CompletedFollowUpPageSchema = z.strictObject({
  items: z.array(CompletedFollowUpSchema),
  total: z.number().int().nonnegative(),
});

/* ------------------------------------------------------------ assignment */

/** Assign or reassign leads to one salesperson; each lead reports its own result. */
export const AssignLeadsSchema = z.strictObject({
  leadIds: z.array(RecordIdSchema).min(1).max(100),
  assignedToAccountId: z.string().min(1).max(200),
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type AssignLeads = z.infer<typeof AssignLeadsSchema>;

export const AssignmentHistoryQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
  from: BusinessDateSchema.optional(),
  to: BusinessDateSchema.optional(),
});
export type AssignmentHistoryQuery = z.infer<typeof AssignmentHistoryQuerySchema>;

export const AssignmentHistorySchema = z.strictObject({
  items: z.array(
    z.strictObject({
      changeId: z.string(),
      leadId: z.string(),
      leadNumber: z.string().optional(),
      leadName: z.string(),
      fromAccountId: z.string().optional(),
      toAccountId: z.string(),
      actorAccountId: z.string(),
      reason: z.string(),
      occurredAt: InstantSchema,
    }),
  ),
  total: z.number().int().nonnegative(),
});
export type AssignmentHistory = z.infer<typeof AssignmentHistorySchema>;

/* ----------------------------------------------------------- CRM settings */

/** The reference lists a Sales Admin keeps; codes in use stay, as in the general list screen. */
export const CRM_REFERENCE_LISTS = [
  'unitTypes',
  'usageTypes',
  'leadSources',
  'lossReasons',
] as const satisfies readonly (typeof REFERENCE_LISTS)[number][];
export type CrmReferenceList = (typeof CRM_REFERENCE_LISTS)[number];
export const CrmReferenceListSchema = z.enum(CRM_REFERENCE_LISTS);

export const CrmSettingsSchema = z.strictObject({
  /** Consecutive unanswered attempts that make a lead "repeated no answer"; null = not configured. */
  repeatedNoAnswerThreshold: z.number().int().min(2).max(50).nullable(),
  /** Days without any recorded activity that make a lead "no activity"; null = not configured. */
  noActivityDays: z.number().int().min(1).max(365).nullable(),
});
export type CrmSettings = z.infer<typeof CrmSettingsSchema>;

export const UpdateCrmSettingsSchema = CrmSettingsSchema.partial().extend({
  reason: z.string().trim().min(3).max(500),
});
export type UpdateCrmSettings = z.infer<typeof UpdateCrmSettingsSchema>;

/* --------------------------------------------------------------- targets */

export const TARGET_METRICS = [
  'leadsAssigned',
  'callAttempts',
  'contactedLeads',
  'interestedLeads',
  'completedFollowUps',
  'meetings',
  'visits',
  'confirmedReservations',
  'activatedContracts',
] as const;
export const TargetMetricSchema = z.enum(TARGET_METRICS);
export type TargetMetric = z.infer<typeof TargetMetricSchema>;

export const MonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: 'MONTH_EXPECTED' });

export const SalesTargetSchema = z.strictObject({
  accountId: z.string(),
  month: MonthSchema,
  metric: TargetMetricSchema,
  value: z.number().int().positive(),
  version: z.number().int().positive(),
  updatedAt: InstantSchema,
  updatedBy: z.string(),
});
export type SalesTarget = z.infer<typeof SalesTargetSchema>;

export const SetSalesTargetSchema = z.strictObject({
  accountId: z.string().min(1).max(200),
  month: MonthSchema,
  metric: TargetMetricSchema,
  /** Null clears the target: the report then says "not configured". */
  value: z.number().int().positive().max(10_000_000).nullable(),
  reason: z.string().trim().min(3).max(500),
});
export type SetSalesTarget = z.infer<typeof SetSalesTargetSchema>;

export const SalesTargetListSchema = z.strictObject({ items: z.array(SalesTargetSchema) });

/* --------------------------------------------------------------- reports */

export const SalesReportQuerySchema = z
  .strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    accountId: z.string().min(1).max(200).optional(),
    projectId: RecordIdSchema.optional(),
    source: z.enum(LEAD_SOURCES).optional(),
  })
  .refine((query) => query.from <= query.to, { message: 'RANGE_INVERTED' });
export type SalesReportQuery = z.infer<typeof SalesReportQuerySchema>;

export const MonthlyReportQuerySchema = z.strictObject({
  month: MonthSchema,
  accountId: z.string().min(1).max(200).optional(),
  projectId: RecordIdSchema.optional(),
  source: z.enum(LEAD_SOURCES).optional(),
});
export type MonthlyReportQuery = z.infer<typeof MonthlyReportQuerySchema>;

export const REPORT_FILE_FORMATS = ['pdf', 'xlsx', 'csv'] as const;
export const REPORT_LOCALES = ['ar', 'en'] as const;

export const SalesReportFileQuerySchema = z
  .strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    accountId: z.string().min(1).max(200).optional(),
    projectId: RecordIdSchema.optional(),
    source: z.enum(LEAD_SOURCES).optional(),
    format: z.enum(REPORT_FILE_FORMATS),
    locale: z.enum(REPORT_LOCALES),
  })
  .refine((query) => query.from <= query.to, { message: 'RANGE_INVERTED' });
export type SalesReportFileQuery = z.infer<typeof SalesReportFileQuerySchema>;

export const MonthlyReportFileQuerySchema = z.strictObject({
  month: MonthSchema,
  accountId: z.string().min(1).max(200).optional(),
  projectId: RecordIdSchema.optional(),
  source: z.enum(LEAD_SOURCES).optional(),
  format: z.enum(REPORT_FILE_FORMATS),
  locale: z.enum(REPORT_LOCALES),
});
export type MonthlyReportFileQuery = z.infer<typeof MonthlyReportFileQuerySchema>;

/** A ratio as a decimal string, or null when the denominator is zero — never a false zero. */
const RateSchema = z.string().nullable();

export const SalesPerformanceRowSchema = z.strictObject({
  accountId: z.string(),
  /** Open leads this person holds now, whatever the period. */
  leadsCurrentlyAssigned: z.number().int().nonnegative(),
  /** Different leads that reached this person inside the period. */
  leadsAssigned: z.number().int().nonnegative(),
  /** Calls this person recorded inside the period; staff-recorded, not telephony-verified. */
  callAttempts: z.number().int().nonnegative(),
  /** Different leads this person recorded any attempt on. */
  attemptedLeads: z.number().int().nonnegative(),
  /** Different leads this person recorded as actually reached. */
  contactedLeads: z.number().int().nonnegative(),
  /** contactedLeads ÷ attemptedLeads. */
  contactRate: RateSchema,
  /** Different leads this person moved to Interested. */
  interestedLeads: z.number().int().nonnegative(),
  completedFollowUps: z.number().int().nonnegative(),
  onTimeFollowUps: z.number().int().nonnegative(),
  /** onTimeFollowUps ÷ completedFollowUps. */
  onTimeRate: RateSchema,
  meetingsScheduled: z.number().int().nonnegative(),
  meetings: z.number().int().nonnegative(),
  visitsScheduled: z.number().int().nonnegative(),
  visits: z.number().int().nonnegative(),
  /** Confirmed in the period and not since cancelled, credited to the sales owner. */
  confirmedReservations: z.number().int().nonnegative(),
  /** Activated in the period and not since cancelled, credited to the sales owner. */
  activatedContracts: z.number().int().nonnegative(),
  /** confirmedReservations ÷ leadsAssigned. A win counts once: a contract is not a second win. */
  reservationRate: RateSchema,
  /** activatedContracts ÷ confirmedReservations. */
  contractRate: RateSchema,
  /** Target and achievement per metric; empty target = not configured. */
  targets: z.array(
    z.strictObject({
      metric: TargetMetricSchema,
      target: z.number().int().positive().nullable(),
      actual: z.number().int().nonnegative(),
      achievement: RateSchema,
    }),
  ),
});
export type SalesPerformanceRow = z.infer<typeof SalesPerformanceRowSchema>;

export const REPORT_LIMITATIONS = [
  /** Calls recorded before outcomes existed count as attempts, never as successful contacts. */
  'callsWithoutOutcome',
  /** Follow-ups completed before scheduled-time records existed are not in the on-time figure. */
  'followUpHistoryStartsAt',
  /** Targets are monthly: a period that is not one calendar month shows none. */
  'targetsNeedWholeMonth',
] as const;
export type ReportLimitation = (typeof REPORT_LIMITATIONS)[number];

export const SalesPerformanceReportSchema = z.strictObject({
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  generatedAt: InstantSchema,
  rows: z.array(SalesPerformanceRowSchema),
  totals: SalesPerformanceRowSchema.omit({ accountId: true, targets: true }),
  limitations: z.array(z.enum(REPORT_LIMITATIONS)),
});
export type SalesPerformanceReport = z.infer<typeof SalesPerformanceReportSchema>;

export const MonthlySalesReportSchema = SalesPerformanceReportSchema.extend({
  month: MonthSchema,
  newLeads: z.number().int().nonnegative(),
  bySource: z.array(
    z.strictObject({
      source: z.enum(LEAD_SOURCES),
      count: z.number().int().nonnegative(),
    }),
  ),
  /** Leads handed to someone at creation, or by a person or a rule afterwards. */
  assignments: z.number().int().nonnegative(),
  /** Of them: moved from one salesperson to another. */
  reassignments: z.number().int().nonnegative(),
  /** Follow-ups overdue right now: the state today, not a historical figure. */
  overdueNow: z.number().int().nonnegative(),
});
export type MonthlySalesReport = z.infer<typeof MonthlySalesReportSchema>;

/* ------------------------------------------------- reservations and contracts */

export const DEAL_VIEWS = ['all', 'confirmed', 'contracts', 'cancelled'] as const;
export type DealView = (typeof DEAL_VIEWS)[number];

export const DealQuerySchema = z
  .strictObject({
    view: z.enum(DEAL_VIEWS).default('all'),
    from: BusinessDateSchema.optional(),
    to: BusinessDateSchema.optional(),
    salesOwnerAccountId: z.string().min(1).max(200).optional(),
    projectId: RecordIdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
  })
  .refine((query) => !query.from || !query.to || query.from <= query.to, {
    message: 'RANGE_INVERTED',
  });
export type DealQuery = z.infer<typeof DealQuerySchema>;

export const PAYMENT_PROGRESS_STATES = ['notPaid', 'partial', 'paid'] as const;

/**
 * One customer's reservation and its contract, for the sales view. Money received comes from posted
 * receipts and the contract's own schedule, never from a typed deposit or a contract's status.
 */
export const DealSchema = z.strictObject({
  reservationId: RecordIdSchema,
  reservationNumber: z.string(),
  customerId: RecordIdSchema,
  customerName: z.string().optional(),
  salesOwnerAccountId: z.string(),
  projectId: RecordIdSchema,
  projectName: z.strictObject({ ar: z.string(), en: z.string() }).optional(),
  unitId: RecordIdSchema,
  unitCode: z.string().optional(),
  agreedPrice: MoneySchema,
  reservedOn: BusinessDateSchema,
  confirmedOn: BusinessDateSchema.optional(),
  agreedDeposit: MoneySchema,
  depositReceived: MoneySchema,
  /** Against the contract's total once a contract is active; against the deposit before that. */
  paidAmount: MoneySchema,
  dueAmount: MoneySchema,
  paymentProgress: z.enum(PAYMENT_PROGRESS_STATES),
  reservationState: z.string(),
  contractId: RecordIdSchema.optional(),
  contractNumber: z.string().optional(),
  contractState: z.string().optional(),
  contractedOn: BusinessDateSchema.optional(),
  cancelled: z.boolean(),
});
export type Deal = z.infer<typeof DealSchema>;

export const DealPageSchema = z.strictObject({
  items: z.array(DealSchema),
  total: z.number().int().nonnegative(),
});
export type DealPage = z.infer<typeof DealPageSchema>;

export const SALES_ADMIN_AUDIT_ACTIONS = {
  representativeAdded: 'crm.team.representativeAdded',
  representativeRefused: 'crm.team.representativeRefused',
  targetSet: 'crm.target.set',
  settingsChanged: 'crm.settings.changed',
  reportExported: 'crm.report.exported',
} as const;
